import { createRouter, createWebHistory } from 'vue-router';
import { ref } from 'vue';
import CryptoJS from 'crypto-js';
import { isDevelopment } from '@/utils/envUtils';

// 加密密钥
const SECRET_KEY = 'X#9k$mP2@vL5nQ8*rT4wZ7&jH3bN6yF';

// 加密函数
function encryptData(data) {
    if (!data || Object.keys(data).length === 0) return '';
    try {
        // 将数据转换为 JSON 字符串
        const str = JSON.stringify(data);
        // 使用 UTF8 编码
        const wordArray = CryptoJS.enc.Utf8.parse(str);
        // 加密
        const encrypted = CryptoJS.AES.encrypt(wordArray, SECRET_KEY).toString();
        // URL 安全的 Base64
        return encrypted
            .replace(/\+/g, '-')
            .replace(/\//g, '_')
            .replace(/=/g, '~');
    } catch (error) {
        console.error('加密失败:', error);
        return '';
    }
}

// 解密函数
function decryptData(encrypted) {
    if (!encrypted || encrypted.trim() === '') return null;
    try {
        // 还原 Base64
        const restored = encrypted
            .replace(/-/g, '+')
            .replace(/_/g, '/')
            .replace(/~/g, '=');

        // 解密
        const bytes = CryptoJS.AES.decrypt(restored, SECRET_KEY);
        // 转换为 UTF8 字符串
        const decrypted = bytes.toString(CryptoJS.enc.Utf8);

        if (!decrypted) return null;

        // 尝试解析 JSON
        try {
            return JSON.parse(decrypted);
        } catch (e) {
            console.error('JSON 解析失败:', e);
            return null;
        }
    } catch (error) {
        console.error('解密失败:', error);
        return null;
    }
}

// 路由配置
const routes = [
    { path: '/', name: 'Home', component: () => import('../views/Home/index.vue') },
    { path: '/login', name: 'login', component: () => import('../views/Login/index.vue') },
    { path: '/register', name: 'register', component: () => import('../views/Register/index.vue') },
    {
        path: '/flight',
        name: 'flight',
        component: () => import('../views/Flight/index.vue'),
        children: [
            {
                path: 'flightChoose',
                name: 'flightChoose',
                component: () => import('../views/Flight/ChooseFlight/index.vue') // 选择航班
            },
            {
                path: 'choosePassenger',
                name: 'choosePassenger',
                component: () => import('../views/Flight/ChoosePassenger/index.vue') // 选择乘客
            },
            {
                path: 'orderConfirm',
                name: 'orderConfirm',
                component: () => import('../views/Flight/OrderConfirm/index.vue') // 订单确认
            },
            {
                path: 'payment',
                name: 'payment',
                component: () => import('../views/Flight/Payment/index.vue')
            },
            {
                path: 'payFinish',
                name: 'payFinish',
                component: () => import('../views/Flight/PayFinish/index.vue') // 支付完成
            },
        ]},
    {   path: '/order',
        name: 'orderDetail',
        component: () => import('../views/Order/OrderDetail/index.vue')
    },
    {   path: '/orderSearch',
        name: 'orderSearch',
        component: () => import('../views/Order/OrderSearch/index.vue')
    },
    {   path: '/ticketDetail',
        name: 'ticketDetail',
        component: () => import('../views/Order/TicketDetail/index.vue')
    },
    {
        path: '/self',
        name: 'self',
        component: () => import('../views/Self/index.vue'),
        children: [
            {
                path: '',
                name: 'selfCenter',
                redirect: { name: 'userInfo' }
            },
            {
                path: 'settings',
                name: 'selfSettings',
                redirect: { name: 'userInfo' },
                component: () => import('../views/Self/Settings/index.vue'),
                children: [
                    {
                        path: 'account',
                        name: 'userInfo',
                        component: () => import('../views/Self/Settings/UserInfo.vue')
                    },
                    {
                        path: 'passengers',
                        name: 'passengersManagement',
                        component: () => import('../views/Self/Settings/Passengers.vue')
                    }
                ]
            },
            {
                path: 'orders',
                name: 'selfOrders',
                redirect: { name: 'selfOrdersList' },
                component: () => import('../views/Self/Orders/index.vue'),
                children: [
                    {
                        path: 'list',
                        name: 'selfOrdersList',
                        component: () => import('../views/Self/Orders/myOrders.vue')
                    },
                    {
                        path: 'tickets',
                        name: 'selfOrdersTickets',
                        component: () => import('../views/Self/Orders/myTickets.vue')
                    }
                ]
            }
        ]
    },
    { path: '/BaiduMap', name: 'BaiduMap', component: () => import('../views/Map/BaiduMap.vue') },
    {
        path: '/checkTicket',
        name: 'checkTicket',
        component: () => import('../views/CheckTicket/index.vue')
    }
];

// 创建响应式的 previousRoute
export const previousRoute = ref(null);

// 创建路由实例
const router = createRouter({
    history: createWebHistory(),
    routes,
    scrollBehavior(to, from, savedPosition) {
        return { top: 0 };
    }
});

router.beforeEach(async (to, from, next) => {
    previousRoute.value = from;

    // 如果有加密的查询参数
    if (to.query.q && to.query.q !== '') {
        try {
            // 解密
            const decryptedData = decryptData(to.query.q);
            if (decryptedData) {
                // 使用解密后的数据替换原查询参数
                next({
                    path: to.path,
                    query: decryptedData,
                    replace: true
                });
                return;
            }
        } catch (error) {
            console.error('路由守卫解密失败:', error);
        }
    }
    next();
});

router.afterEach((to, from) => {
    // 开发环境不加密路由参数
    if (isDevelopment()) {
        return;
    }

    // 如果有查询参数且不是已加密的
    if (to.query && Object.keys(to.query).length > 0 && !to.query.q) {
        try {
            // 加密查询参数
            const encryptedData = encryptData(to.query);
            if (encryptedData) {
                // 更新URL，但不触发新的导航
                const newQuery = { q: encryptedData };
                const newPath = router.resolve({
                    path: to.path,
                    query: newQuery
                }).href;
                window.history.replaceState(null, '', newPath);
            }
        } catch (error) {
            console.error('路由守卫加密失败:', error);
        }
    }
});

export default router;